import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import * as ItemComponentActions from "../../../actions/itemComponents";
import * as ItemComponentTypeActions from "../../../actions/itemComponentType";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { InlineLoading } from "../../../components/common";
import { FormFilterInput } from "../../../components/form";
import { TruncatedCell } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import TrialGroupLookupsLayout from "../../../containers/TrialInstitutionLookups/TrialGroupLookupsLayout/TrialGroupLookupsLayout";
import TrialInstitutionLookupsLayout from "../../../containers/TrialInstitutionLookups/TrialInstitutionLookupsLayout/TrialInstitutionLookupsLayout";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    FormComponentsBreadcrumbs,
    GroupItemComponentsBreadcrumbs,
    InstitutionItemComponentsBreadcrumbs,
    ProjectItemComponentsBreadcrumbs,
    TrialGroupItemComponentsBreadcrumbs,
    TrialInstitutionItemComponentsBreadcrumbs,
    TrialItemComponentsBreadcrumbs,
} from "../../../helpers/breadcrumbHelper";
import {
    SettingsGroupsSubMenu,
    SettingsInstitutionsSubMenu,
    SettingsProjectsSubMenu,
    SettingsQASubMenu,
    SettingsTrialGroupSubMenu,
    SettingsTrialInstitutionSubMenu,
    SettingsTrialSubMenu,
} from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import {
    GroupSettingsLayout,
    InstitutionLookupsLayout,
    ProjectLookupsLayout,
    QALookupsLayout,
    TrialLookupsLayout,
} from "../../index";

import "./ItemComponentsPage.scss";

interface ItemComponentsPageProps {
    itemComponents: Dtos.ItemComponent[];
    itemComponentTypes: Dtos.ItemComponentType[];
    formFilter: IFormFilter;
    loadingItemComponents: boolean;
    loadItemComponentsSuccess: boolean;
    loadItemComponentsFailure: boolean;
    loadItemComponentsErrorCode: string;
    loadingItemComponentTypes: boolean;
    loadItemComponentTypesSuccess: boolean;
    loadItemComponentTypesFailure: boolean;
    loadItemComponentTypesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearItemComponents: ItemComponentActions.IItemComponentClearActionCreator;
    loadItemComponents: ItemComponentActions.IItemComponentLoadActionCreator;
    clearItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeClearActionCreator;
    loadItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeLoadActionCreator;
    filter: ItemComponentActions.IItemComponentFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const ItemComponentsPage: FC<ItemComponentsPageProps> = ({
    itemComponents,
    itemComponentTypes,
    formFilter,
    loadingItemComponents,
    loadItemComponentsSuccess,
    loadItemComponentsFailure,
    loadItemComponentsErrorCode,
    loadingItemComponentTypes,
    loadItemComponentTypesSuccess,
    loadItemComponentTypesFailure,
    loadItemComponentTypesErrorCode,
    permissions,
    pageSize,
    clearItemComponents,
    loadItemComponents,
    clearItemComponentTypes,
    loadItemComponentTypes,
    filter,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- States --
    const [rootFolder, setRootFolder] = useState<string>("/settings/trial/itemcomponents");
    const [qaItemComponents, setQaItemComponents] = useState<boolean>(false);
    const [itemComponentTypeId, setItemComponentTypeId] = useState<number>(0);

    //
    // -- Variables --
    const showItemComponents =
        !loadingItemComponents &&
        loadItemComponentsSuccess &&
        !loadingItemComponentTypes &&
        loadItemComponentTypesSuccess;

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();


    // Load item components, types and set states
    useEffect(() => {
        if (!itemComponentTypes && !loadingItemComponentTypes) {
            loadItemComponentTypes();
        }

        let qaItemComponentsTemp: boolean = location.pathname.indexOf("/qa/") > -1;
        setQaItemComponents(qaItemComponentsTemp);

        let rootFolderTemp: string =
            location.pathname.indexOf("/group/") > -1
                ? "/settings/group/itemcomponents"
                : location.pathname.indexOf("/qa/") > -1
                ? "/settings/qa/formcomponents"
                : location.pathname.indexOf("/project/") > -1
                ? "/settings/project/itemcomponents"
                : location.pathname.indexOf("/institution/") > -1
                ? "/settings/institution/itemcomponents"
                : location.pathname.indexOf("/trialinstitution/") > -1
                ? "/settings/trialinstitution/itemcomponents"
                : location.pathname.indexOf("/trialgroup/") > -1
                ? "/settings/trialgroup/itemcomponents"
                : "/settings/trial/itemcomponents";
        setRootFolder(rootFolderTemp);

        if (itemComponentTypes) {
            let itemComponentTypeIdTemp: number =
                location.pathname.indexOf("/group/") > -1
                    ? itemComponentTypes.find((x) => x.value === "Group")?.id
                    : location.pathname.indexOf("/project/") > -1
                    ? itemComponentTypes.find((x) => x.value === "Project")?.id
                    : location.pathname.indexOf("/institution/") > -1
                    ? itemComponentTypes.find((x) => x.value === "Institution")?.id
                    : location.pathname.indexOf("/trialinstitution/") > -1
                    ? itemComponentTypes.find((x) => x.value === "TrialInstitution")?.id
                    : location.pathname.indexOf("/trialgroup/") > -1
                    ? itemComponentTypes.find((x) => x.value === "TrialGroup")?.id
                    : location.pathname.indexOf("/trial/") > -1
                    ? itemComponentTypes.find((x) => x.value === "Trial")?.id
                    : 0;
            setItemComponentTypeId(itemComponentTypeIdTemp);
        }

        if (!loadingItemComponents && !loadItemComponentsSuccess && (qaItemComponents || itemComponentTypeId > 0)) {
            loadItemComponents(qaItemComponents, itemComponentTypeId);
        }

        // Set correct drawer and bread crumb items
        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Group")?.id) {
            // Group Item Components
            setDrawerItems(SettingsGroupsSubMenu(permissions));
            setBreadcrumbs(GroupItemComponentsBreadcrumbs());
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Project")?.id) {
            // Project Item Components
            setDrawerItems(SettingsProjectsSubMenu(permissions));
            setBreadcrumbs(ProjectItemComponentsBreadcrumbs());
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Institution")?.id) {
            // Institution Item Components
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(InstitutionItemComponentsBreadcrumbs());
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialInstitution")?.id) {
            // Trial Institution Item Components
            setDrawerItems(SettingsTrialInstitutionSubMenu(permissions));
            setBreadcrumbs(TrialInstitutionItemComponentsBreadcrumbs());
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialGroup")?.id) {
            // Trial Group Item Components
            setDrawerItems(SettingsTrialGroupSubMenu(permissions));
            setBreadcrumbs(TrialGroupItemComponentsBreadcrumbs());
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Trial")?.id) {
            // Trial Item Components
            setDrawerItems(SettingsTrialSubMenu(permissions));
            setBreadcrumbs(TrialItemComponentsBreadcrumbs());
        } else if (qaItemComponents) {
            // Form Components
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(FormComponentsBreadcrumbs());
        }
    }, [
        location,
        setRootFolder,
        setQaItemComponents,
        setItemComponentTypeId,
        qaItemComponents,
        itemComponentTypeId,
        permissions,
        loadItemComponents,
        setDrawerItems,
        setBreadcrumbs,
        SettingsGroupsSubMenu,
        GroupItemComponentsBreadcrumbs,
        SettingsProjectsSubMenu,
        ProjectItemComponentsBreadcrumbs,
        SettingsQASubMenu,
        FormComponentsBreadcrumbs,
        SettingsTrialSubMenu,
        TrialItemComponentsBreadcrumbs,
        SettingsInstitutionsSubMenu,
        InstitutionItemComponentsBreadcrumbs,
        loadingItemComponents,
        itemComponentTypes,
        loadingItemComponentTypes,
        loadItemComponentTypes,
        itemComponents,
        loadItemComponentsSuccess,
        SettingsTrialInstitutionSubMenu,
        TrialInstitutionItemComponentsBreadcrumbs,
        SettingsTrialGroupSubMenu,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadItemComponentsFailure) {
            navigate(`/error/${loadItemComponentsErrorCode}`);
        }
        if (loadItemComponentTypesFailure) {
            navigate(`/error/${loadItemComponentTypesErrorCode}`);
        }
    }, [
        navigate,
        loadItemComponentsFailure,
        loadItemComponentsErrorCode,
        loadItemComponentTypesFailure,
        loadItemComponentTypesErrorCode,
    ]);

    // Cleanup effect on component unmount
    useEffect(() => {
        return () => {
            clearItemComponents();
            clearItemComponentTypes();
        };
    }, [clearItemComponents, clearItemComponentTypes]);

    useEffect(() => {
        if (itemComponentTypeId) {
            setItemComponentTypeId(itemComponentTypeId);
        }
    }, [itemComponentTypeId, setItemComponentTypeId]);

    const createTable = useMemo(() => {
        if (!itemComponents || itemComponents.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                        </div>
                        <span>
                            There are currently no item components to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = itemComponents.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={itemComponents}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? pageSize : itemComponents.length}
                    pageSize={showPagination ? pageSize : itemComponents.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no item components to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                navigate(`${rootFolder}/${rowInfo.original.id}`);
                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Light.ITEMCOLLECTION} fixedWidth />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Component Name",
                            accessor: "name",
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                        {
                            id: "description",
                            Header: "Description",
                            accessor: "description",
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                        {
                            id: "itemComponentType.value",
                            Header: "Component Type",
                            accessor: "itemComponentType.value",
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [
        itemComponents,
        pageSize,
        PAGE_SIZE_OPTIONS,
        setPageSize,
        navigate,
        rootFolder,
    ]);

    const renderContent = useMemo(() => {
        let canCreate: boolean = false;
        let label = "";
        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Group")?.id) {
            label = "Group Item Components";
            canCreate = AuthenticationHelper.hasPermission(
                permissions,
                Dtos.Permission.GroupItemComponentsCreate
            );
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Project")?.id) {
            label = "Project Item Components";
            canCreate = AuthenticationHelper.hasPermission(
                permissions,
                Dtos.Permission.ProjectItemComponentsCreate
            );
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Institution")?.id) {
            label = "Institution Item Components";
            canCreate = AuthenticationHelper.hasPermission(
                permissions,
                Dtos.Permission.InstitutionItemComponentsCreate
            );
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialInstitution")?.id) {
            label = "Trial Institution Item Components";
            canCreate = AuthenticationHelper.hasPermission(
                permissions,
                Dtos.Permission.TrialInstitutionItemComponentsCreate
            );
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialGroup")?.id) {
            label = "Trial Group Item Components";
            canCreate = AuthenticationHelper.hasPermission(
                permissions,
                Dtos.Permission.TrialGroupItemComponentsCreate
            );
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Trial")?.id) {
            label = "Trial Item Components";
            canCreate = AuthenticationHelper.hasPermission(
                permissions,
                Dtos.Permission.ItemComponentsCreate
            );
        } else if (qaItemComponents) {
            label = "Form Components";
            canCreate = AuthenticationHelper.hasPermission(
                permissions,
                Dtos.Permission.FormComponentsCreate
            );
        }

        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>{label}</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {canCreate && (
                            <Link
                                className={"btn btn-primary"}
                                url={rootFolder + "/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showItemComponents ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showItemComponents ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        itemComponentTypeId,
        qaItemComponents,
        permissions,
        formFilter,
        filter,
        rootFolder,
        showItemComponents,
        createTable,
    ]);

    const renderLayout = useMemo(() => {
        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Group")?.id) {
            return (
                <GroupSettingsLayout
                    loading={loadingItemComponents}
                    permission={Dtos.Permission.GroupItemComponentsView}
                >
                    {renderContent}
                </GroupSettingsLayout>
            );
        }

        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Project")?.id) {
            return (
                <ProjectLookupsLayout
                    loading={loadingItemComponents}
                    permission={Dtos.Permission.ProjectItemComponentsView}
                >
                    {renderContent}
                </ProjectLookupsLayout>
            );
        }

        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Institution")?.id) {
            return (
                <InstitutionLookupsLayout
                    loading={loadingItemComponents}
                    permission={Dtos.Permission.InstitutionItemComponentsView}
                >
                    {renderContent}
                </InstitutionLookupsLayout>
            );
        }

        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialInstitution")?.id) {
            return (
                <TrialInstitutionLookupsLayout
                    loading={loadingItemComponents}
                    permission={
                        Dtos.Permission.TrialInstitutionItemComponentsView
                    }
                >
                    {renderContent}
                </TrialInstitutionLookupsLayout>
            );
        }

        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialGroup")?.id) {
            return (
                <TrialGroupLookupsLayout
                    loading={loadingItemComponents}
                    permission={Dtos.Permission.TrialGroupItemComponentsView}
                >
                    {renderContent}
                </TrialGroupLookupsLayout>
            );
        }

        if (qaItemComponents) {
            return (
                <QALookupsLayout
                    loading={loadingItemComponents}
                    permission={Dtos.Permission.FormComponentsView}
                >
                    {renderContent}
                </QALookupsLayout>
            );
        }

        return (
            <TrialLookupsLayout
                permission={Dtos.Permission.ItemComponentsView}
                loading={loadingItemComponents}
            >
                {renderContent}
            </TrialLookupsLayout>
        );
    }, [itemComponentTypeId, loadingItemComponents, renderContent]);

    return <>{renderLayout}</>;
};

const mapStateToProps = (
    state: ITmdState
): Partial<ItemComponentsPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    let itemComponentTypes =
        state.itemComponentTypes.formData instanceof Array
            ? state.itemComponentTypes.formData
            : undefined;
    return {
        itemComponents: state.itemComponent.formFilteredData!,
        itemComponentTypes: itemComponentTypes,
        formFilter: state.itemComponent.formFilter,
        loadingItemComponents: state.itemComponent.loadState && state.itemComponent.loadState.status == RequestState.Pending,
        loadItemComponentsSuccess: state.itemComponent.loadState && state.itemComponent.loadState.status == RequestState.Success,
        loadItemComponentsFailure: state.itemComponent.loadState && state.itemComponent.loadState.status == RequestState.Failure,
        loadItemComponentsErrorCode: state.itemComponent.loadState.errorCode,
        loadingItemComponentTypes: state.itemComponentTypes.loadState && state.itemComponentTypes.loadState.status == RequestState.Pending,
        loadItemComponentTypesSuccess: state.itemComponentTypes.loadState && state.itemComponentTypes.loadState.status == RequestState.Success,
        loadItemComponentTypesFailure: state.itemComponentTypes.loadState && state.itemComponentTypes.loadState.status == RequestState.Failure,
        loadItemComponentTypesErrorCode: state.itemComponentTypes.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<ItemComponentsPageProps> => {
    return {
        loadItemComponents: bindActionCreators(ItemComponentActions.LoadItemComponent, dispatch),
        clearItemComponents: bindActionCreators(ItemComponentActions.Clear, dispatch),
        loadItemComponentTypes: bindActionCreators(ItemComponentTypeActions.LoadItemComponentType, dispatch),
        clearItemComponentTypes: bindActionCreators(ItemComponentTypeActions.Clear, dispatch),
        filter: bindActionCreators(ItemComponentActions.Filter, dispatch),
        setPageSize: bindActionCreators(PagingActions.PageSizeChanged, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ItemComponentsPage);
